import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import holidayService from '../services/holidayService';
import './EmployeeManagement.css';

const HolidayManagement = () => {
  const { hasModulePermission } = useAuth();
  const canCreate = hasModulePermission('HOLIDAYS', 'create');
  const canUpdate = hasModulePermission('HOLIDAYS', 'update');
  const canDelete = hasModulePermission('HOLIDAYS', 'delete');
  
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showForm, setShowForm] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterType, setFilterType] = useState('ALL');
  const [filterRecurring, setFilterRecurring] = useState('ALL');

  const [formData, setFormData] = useState({
    name: '',
    date: '',
    type: 'PAID',
    isRecurring: false,
    isNational: true,
    description: '',
  });

  useEffect(() => {
    loadHolidays();
  }, [filterYear, filterType]);

  const loadHolidays = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { year: filterYear };
      if (filterType !== 'ALL') {
        params.type = filterType;
      }
      const response = await holidayService.getAll(params);
      let holidaysData = response.data || [];
      
      // Filtrer par récurrent si nécessaire
      if (filterRecurring !== 'ALL') {
        const isRecurring = filterRecurring === 'YES';
        holidaysData = holidaysData.filter(h => h.isRecurring === isRecurring);
      }
      
      setHolidays(holidaysData);
      setPage(1);
    } catch (err) {
      setError('Erreur lors du chargement des jours chômés');
      console.error('Error loading holidays:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(holidays.length / pageSize));
  const displayedHolidays = holidays.slice((page - 1) * pageSize, page * pageSize);
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation basique
    if (!formData.name || !formData.date) {
      setError('Le nom et la date sont obligatoires');
      return;
    }
    
    try {
      // Préparer les données pour l'envoi
      const holidayData = {
        name: formData.name.trim(),
        date: formData.date, // Format YYYY-MM-DD (format natif de input type="date")
        type: formData.type,
        isRecurring: formData.isRecurring || false,
        isNational: formData.isNational !== undefined ? formData.isNational : true,
        description: formData.description ? formData.description.trim() : null,
      };
      
      if (selectedHoliday) {
        await holidayService.update(selectedHoliday.id, holidayData);
      } else {
        await holidayService.create(holidayData);
      }
      resetForm();
      loadHolidays();
      setError(''); // Clear error on success
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Erreur lors de l\'enregistrement';
      setError(errorMessage);
      console.error('Error saving holiday:', err);
      console.error('Error response:', err.response);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce jour chômé ?')) {
      return;
    }
    
    try {
      await holidayService.delete(id);
      loadHolidays();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleEdit = (holiday) => {
    setSelectedHoliday(holiday);
    setFormData({
      name: holiday.name || '',
      date: holiday.date || '',
      type: holiday.type || 'PAID',
      isRecurring: holiday.isRecurring || false,
      isNational: holiday.isNational !== undefined ? holiday.isNational : true,
      description: holiday.description || '',
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      date: '',
      type: 'PAID',
      isRecurring: false,
      isNational: true,
      description: '',
    });
    setSelectedHoliday(null);
    setShowForm(false);
  };

  const handleInitializeDefault = async () => {
    if (!window.confirm(`Initialiser les jours fériés tunisiens pour l'année ${filterYear} ?`)) {
      return;
    }
    
    try {
      await holidayService.initializeDefault(filterYear);
      loadHolidays();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'initialisation');
    }
  };

  const handleApplyRecurring = async () => {
    if (!window.confirm(`Appliquer les jours chômés récurrents pour l'année ${filterYear} ?`)) {
      return;
    }
    
    try {
      await holidayService.applyRecurring(filterYear);
      loadHolidays();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'application');
    }
  };

  return (
    <div className="employee-management">
      {error && (
        <div className="error-banner" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div className="management-header">
        <h2>Gestion des Jours Chômés</h2>
        <div className="actions">
          {canCreate && (
            <>
              <button className="btn btn-outline" onClick={handleInitializeDefault}>
                Initialiser Jours Fériés
              </button>
              <button className="btn btn-outline" onClick={handleApplyRecurring}>
                Appliquer Récurrents
              </button>
              <button className="btn btn-primary" onClick={() => {
                setSelectedHoliday(null);
                setFormData({
                  name: '',
                  date: '',
                  type: 'PAID',
                  isRecurring: false,
                  isNational: true,
                  description: '',
                });
                setShowForm(true);
              }}>
                + Nouveau Jour Chômé
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="filters" style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <div>
          <label>Année: </label>
          <input
            type="number"
            value={filterYear}
            onChange={(e) => setFilterYear(parseInt(e.target.value))}
            style={{ width: '100px', padding: '0.5rem' }}
          />
        </div>
        <div>
          <label>Type: </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{ padding: '0.5rem' }}
          >
            <option value="ALL">Tous</option>
            <option value="PAID">Payés</option>
            <option value="UNPAID">Non payés</option>
          </select>
        </div>
        <div>
          <label>Récurrent: </label>
          <select
            value={filterRecurring}
            onChange={(e) => setFilterRecurring(e.target.value)}
            style={{ padding: '0.5rem' }}
          >
            <option value="ALL">Tous</option>
            <option value="YES">Oui</option>
            <option value="NO">Non</option>
          </select>
        </div>
      </div>

      {/* Formulaire */}
      {showForm && canCreate && (
        <div className="form-container" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}>
          <h3>{selectedHoliday ? 'Modifier' : 'Créer'} un Jour Chômé</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nom <span className="required">*</span>:</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </div>

            <div className="form-group">
              <label>Date <span className="required">*</span>:</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </div>

            <div className="form-group">
              <label>Type <span className="required">*</span>:</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
                style={{ width: '100%', padding: '0.5rem' }}
              >
                <option value="PAID">Chômé payé</option>
                <option value="UNPAID">Chômé non payé</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isRecurring}
                  onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                />
                {' '}Récurrent (se répète chaque année)
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isNational}
                  onChange={(e) => setFormData({ ...formData, isNational: e.target.checked })}
                />
                {' '}Jour férié national
              </label>
            </div>

            <div className="form-group">
              <label>Description:</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {selectedHoliday ? 'Mettre à jour' : 'Créer'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste */}
      <div className="table-container">
        {loading ? (
          <div>Chargement...</div>
        ) : holidays.length === 0 ? (
          <div className="empty-state">Aucun jour chômé trouvé</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Nom</th>
                <th>Type</th>
                <th>Récurrent</th>
                <th>National</th>
                <th>Description</th>
                {(canUpdate || canDelete) && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {displayedHolidays.map((holiday) => (
                <tr key={holiday.id}>
                  <td>{holiday.formattedDate || holiday.date}</td>
                  <td>{holiday.name}</td>
                  <td>
                    <span className={`badge ${holiday.type === 'PAID' ? 'badge-success' : 'badge-warning'}`}>
                      {holiday.typeLabel || holiday.type}
                    </span>
                  </td>
                  <td>{holiday.isRecurringLabel || (holiday.isRecurring ? 'Oui' : 'Non')}</td>
                  <td>{holiday.isNational ? 'Oui' : 'Non'}</td>
                  <td>{holiday.description || '-'}</td>
                  {(canUpdate || canDelete) && (
                    <td>
                      {canUpdate && (
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => handleEdit(holiday)}
                          style={{ marginRight: '0.5rem' }}
                        >
                          Modifier
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(holiday.id)}
                        >
                          Supprimer
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && holidays.length > 0 && (
        <div className="pagination" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button className="btn btn-secondary" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>
            Précédent
          </button>
          <span>Page {page} / {totalPages}</span>
          <button className="btn btn-secondary" disabled={page === totalPages} onClick={() => handlePageChange(page + 1)}>
            Suivant
          </button>
        </div>
      )}
    </div>
  );
};

export default HolidayManagement;

